import { PayloadAction } from '@reduxjs/toolkit';
import cookie from 'js-cookie';
import { delay, put, select, takeLatest } from 'redux-saga/effects';
import { RootState } from '..';
import { AuthStatus } from '../../types/Auth.types';
import { SelectedData } from '../../types/Editor.types';
import { ToolType } from '../../types/Editor/Tool.types';
import { FloorPlanArtwork } from '../../types/FloorPlan/Artwork.types';
import { Entrance } from '../../types/FloorPlan/Entrance.types';
import { EYE_LINE_HEIGHT_CM_MAX, EYE_LINE_HEIGHT_CM_MIN, FloorPlanDataType, FloorPlanMap, FloorPlanSettings, FloorPlanState } from '../../types/FloorPlan/FloorPlan.types';
import { FloorPlanData } from '../../types/FloorPlan/FloorPlanElement.types';
import { Light } from '../../types/FloorPlan/Light.types';
import { Sculpture } from '../../types/FloorPlan/Sculpture.types';
import { Spot } from '../../types/FloorPlan/Spot.types';
import { Wall } from '../../types/FloorPlan/Wall.types';
// import { callApi } from '../api';
import { cleanUpUnplacedData, getFloorPlanRequest, removeFloorPlanData, saveFloorPlanRequest, setFloorPlanMap, setFloorPlanSettings, setPropertyUuid, setSaveState, startSaveFloorPlanCycle, tryDeleteData } from '../reducers/FloorPlan/FloorPlan';
import { confirmDeleteModalOpen } from '../reducers/UI';
import missingImage from './../../assets/img/missing-image.png'
import { setExhibitionStatus } from '../reducers/Exhibition';
import { updateAllLightsWarning } from '../reducers/FloorPlan/Properties';
import { setShowDemoJsonData } from '../reducers/Editor';
import { Buffer } from 'buffer';

export function* createNewFloorPlanData(data: FloorPlanDataType, id?: string) {
    switch (data) {
        case "Spot":
            const allSpots: { [id: string]: Spot } | undefined =
                yield select((state: RootState) => state.floorPlan.floorPlan?.spots)

            const newSpot = new Spot(id)

            if (allSpots)
                newSpot.name = "Spot " + (Object.keys(allSpots).length + 1)

            return newSpot
        case "Light":
            const allLights: { [id: string]: Light } | undefined =
                yield select((state: RootState) => state.floorPlan.floorPlan?.lights)

            const newLight = new Light(id)

            if (allLights)
                newLight.name = "Light " + (Object.keys(allLights).length + 1)

            return newLight
        case "Sculpture":
            return new Sculpture(id)
        case "Artwork":
            return new FloorPlanArtwork(id)
        case "Wall":

            const allWalls: { [id: string]: Wall } | undefined =
                yield select((state: RootState) => state.floorPlan.floorPlan?.walls)

            const newWall = new Wall(id)

            if (allWalls)
                newWall.name = "Wall " + (Object.keys(allWalls).length + 1)

            return newWall
        default:
            console.warn("createNewFloorPlanData of type " + data + " not developed")
    }
}


function* _cleanUpUnplacedData({ payload: cleanUpType }: PayloadAction<FloorPlanDataType | "All">
) {

    const floorPlanData: FloorPlanMap | null = yield select((state: RootState) => state.floorPlan.floorPlan)
    if (!floorPlanData)
        return

    var cleanUpData: { [id: string]: FloorPlanData } = {}

    switch (cleanUpType) {
        case "Spot":
            cleanUpData = { ...cleanUpData, ...floorPlanData.spots }
            break
        case "Light":
            cleanUpData = { ...cleanUpData, ...floorPlanData.lights }
            break
        case "Sculpture":
            cleanUpData = { ...cleanUpData, ...floorPlanData.sculptures }
            break
        case "Wall":
            cleanUpData = { ...cleanUpData, ...floorPlanData.walls }
            break
        case "All":
            yield put(cleanUpUnplacedData("Light"))
            yield put(cleanUpUnplacedData("Spot"))
            yield put(cleanUpUnplacedData("Sculpture"))
            yield put(cleanUpUnplacedData("Wall"))
            return
        default:
            console.warn("_cleanUpUnplacedData " + cleanUpType + " not developed")
    }

    const entriesToCleanUp = Object.entries(cleanUpData)

    for (let i = 0; i < entriesToCleanUp.length; i++) {
        const [k, v] = entriesToCleanUp[i];

        if (!v.placed) {
            yield put(removeFloorPlanData({ type: cleanUpType, id: k }))
        }
    }
}


export function* watchCleanUpUnplacedData() {
    yield takeLatest(cleanUpUnplacedData, _cleanUpUnplacedData);
}


const mapArtworksWithExhibitionData = (wallsRaw: any) => {
    const artworks: { [id: string]: FloorPlanArtwork } = {}

    for (let i = 0; i < wallsRaw.length; i++) {
        const artworkRaw = wallsRaw[i].artwork
        const wallId = wallsRaw[i].map_ref_uuid

        for (let i = 0; i < artworkRaw.length; i++) {
            const item = artworkRaw[i];
            const artwork: FloorPlanArtwork = new FloorPlanArtwork(item.uuid)

            artwork.x = Number(item.artwork_in_exhibition.x)
            artwork.y = Number(item.artwork_in_exhibition.y)

            artwork.width = Number(item.artwork_in_exhibition.width_in_exhibition)
            artwork.height = Number(item.artwork_in_exhibition.height_in_exhibition)

            artwork.side = item.artwork_in_exhibition.side || 'right'

            artwork.hasFrame = item.artwork_in_exhibition.has_frame === 1
            artwork.frameWidth = Number(item.artwork_in_exhibition.frame_width)
            artwork.frameColor = item.artwork_in_exhibition.frame_color
            artwork.depth = Number(item.artwork_in_exhibition.frame_depth)
            if (item.artwork_in_exhibition.edge_color)
                artwork.edgeColor = item.artwork_in_exhibition.edge_color
            artwork.frameTextureId = item.artwork_in_exhibition.frame_texture_id
            artwork.frameMaterial = item.artwork_in_exhibition.frame_material

            artwork.passepartoutWidth = Number(item.artwork_in_exhibition.passepartout_width)
            artwork.passepartoutColor = item.artwork_in_exhibition.passepartout_color

            artwork.wallId = wallId

            artwork.imageURL = item.photo ? item.photo.medium : missingImage

            if (item.title?.en_EN)
                artwork.title = item.title?.en_EN

            artworks[item.uuid] = artwork
        }
    }

    return artworks
}

const mapWallRawData = (wallsRaw: any) => {
    const walls: { [id: string]: Wall } = {}

    for (let i = 0; i < wallsRaw.length; i++) {
        const item = wallsRaw[i];
        const wall: Wall = new Wall(item.map_ref_uuid)

        wall.name = item.name
        wall.uuid = item.uuid
        wall.origin.x = item.x1
        wall.origin.y = item.y1
        wall.destiny.x = item.x2
        wall.destiny.y = item.y2
        wall.thickness = item.thickness
        wall.color = item.color
        wall.colorBack = item.color_back
        wall.hasColorBack = item.has_color_back === 1
        wall.hasTexture = item.has_texture
        if (typeof (item.region) === 'number')
            wall.region = item.region

        wall.textureURL = item.texture ? item.texture : "EmptyUrlTextureURL"

        wall.computePropertiesFromPoints()

        walls[item.map_ref_uuid] = wall
    }

    return walls
}

const mapSculptureRawData = (sculpturesRaw: any) => {
    const sculptures: { [id: string]: Sculpture } = {}

    for (let i = 0; i < sculpturesRaw.length; i++) {
        const item = sculpturesRaw[i];
        const sculpture: Sculpture = new Sculpture(item.uuid)

        sculpture.uuid = item.uuid
        sculpture.x = Number(item.sculpture_in_exhibition.x)
        sculpture.y = Number(item.sculpture_in_exhibition.y)
        sculpture.z = Number(item.sculpture_in_exhibition.z)
        sculpture.orientation = item.sculpture_in_exhibition.orientation
        sculpture.dimensions = {
            width: Number(item.sculpture_in_exhibition.width_in_exhibition),
            height: Number(item.sculpture_in_exhibition.height_in_exhibition),
            depth: Number(item.sculpture_in_exhibition.depth_in_exhibition),
        }

        // console.log("mapSculptureDataToFloorPlanSculptures", item, sculpture)

        sculptures[item.uuid] = sculpture
    }

    return sculptures
}

const mapLightRawData = (lightsRaw: any) => {
    const lights: { [id: string]: Light } = {}

    for (let i = 0; i < lightsRaw.length; i++) {
        const item = lightsRaw[i]
        const light: Light = new Light(item.map_ref_uuid)

        light.uuid = item.uuid
        light.x = item.x
        light.y = item.y
        light.type = item.type
        light.intensity = item.intensity
        light.color = item.color
        light.orientation = item.orientation
        light.name = item.name
        light.openingAngle = item.opening_angle
        light.blurryHaloPr = item.blurry_halo_pr
        light.verticalAngle = item.vertical_angle

        // console.log("mapLightDataToFloorPlanLights", item, light)
        lights[item.map_ref_uuid] = light
    }

    return lights
}

const mapSpotRawData = (spotsRaw: any) => {
    const spots: { [id: string]: Spot } = {}

    for (let i = 0; i < spotsRaw.length; i++) {
        const item = spotsRaw[i]
        const spot: Spot = new Spot(item.map_ref_uuid)

        spot.uuid = item.uuid
        spot.x = item.x
        spot.y = item.y
        spot.type = item.type
        spot.orientation = item.orientation
        spot.name = item.name
        spot.artworkUUID = item.artwork_uuid

        // console.log("mapSpotDataToFloorPlanSpots", item, spot)
        spots[item.map_ref_uuid] = spot
    }

    return spots
}


const mapEntranceDataToFloorPlanEntrance = (exhibitionData: any): Entrance => {
    const entrance = new Entrance()
    if (exhibitionData.entrance_x !== null)
        entrance.x = exhibitionData.entrance_x

    if (exhibitionData.entrance_y !== null)
        entrance.y = exhibitionData.entrance_y

    if (exhibitionData.entrance_angle !== null)
        entrance.orientation = exhibitionData.entrance_angle
    return entrance
}

// import { isSuccessResponse } from '../utils';

export function* doGetFloorPlan({ payload: mapUuid }: PayloadAction<string | undefined>) {
    // console.log("doGetFloorPlan", mapUuid)

    try {
        if (!mapUuid) {
            return
        }

        // yield put(setExhibitionStatus("Loading"))
        // const mapResponse: AxiosResponse = yield call(callApi, 'GET', `/maps/${mapUuid}`)
        // if (!isSuccessResponse(mapResponse, true)) {
        // console.error(mapResponse)
        // yield put(setExhibitionStatus("Error_Uuid"))
        // return
        // }

        // const offline = true

        var data
        // if (offline)
        data = offlineMap.data
        // else
        // data = mapResponse.data.data

        // console.log(data)

        var floorPlan: FloorPlanMap = {
            id: mapUuid,
            entrance: mapEntranceDataToFloorPlanEntrance(data.exhibition),
            walls: mapWallRawData(data.walls),
            sculptures: mapSculptureRawData(data.sculptures),
            lights: mapLightRawData(data.lights),
            spots: mapSpotRawData(data.spots),
            artworks: mapArtworksWithExhibitionData(data.walls)
        }

        updateAllLightsWarning(floorPlan)
        // console.log("get floorPlan", floorPlan)

        var eyeLineHeight = Number(cookie.get("eyeLineHeight") || EYE_LINE_HEIGHT_CM_MIN)
        if (eyeLineHeight < EYE_LINE_HEIGHT_CM_MIN)
            eyeLineHeight = EYE_LINE_HEIGHT_CM_MIN
        if (eyeLineHeight > EYE_LINE_HEIGHT_CM_MAX)
            eyeLineHeight = EYE_LINE_HEIGHT_CM_MAX

        var lockDegrees = Boolean(cookie.get("lockDegrees") === "true")

        const wallsBaseboard = data.walls[0]?.baseboard === undefined ? true : data.walls[0]?.baseboard

        const floorPlanSettings: FloorPlanSettings = {
            ceilColor: data.ceil_color,
            floorColor: data.floor_color,
            floorHasTexture: data.floor_has_texture === 1,
            floorTextureId: data.floor_texture.id,
            groundColor: data.ground_color,
            hasCeil: data.has_ceil,
            hasFloor: data.has_floor,
            skyColor: data.sky_color,
            tileSize: data.tile_size,
            wallHeight: data.wall_height,
            ambientLightColor: data.ambient_light_color || "#cccccc",
            // wallIncrement: 10,
            wallsBaseboard: wallsBaseboard,
            wallsDefaultColor: data.walls_default_color,
            wallsDefaultTextureURL: data.walls_default_texture_url,
            wallsHaveDefaultTexture: data.walls_have_default_texture === 1,
            eyeLineHeight,
            lockDegrees,
        }


        yield put(setFloorPlanMap(floorPlan))
        yield put(setFloorPlanSettings(floorPlanSettings))
        yield put(setExhibitionStatus("Success_Map"))
        // console.log("finished")
    } catch (error) {
        if (error instanceof Error) {
            console.error(error.message)
        } else {
            console.error('Unexpected error', error);
        }
        yield put(setExhibitionStatus("Error_Unknown"))
        /* 
        yield put(openNotification('An error occurred when getting exhibition map.', 'error'));
        yield put(errorAction(type, error)); */
    }
}

export function* watchGetFloorPlan() {
    yield takeLatest(getFloorPlanRequest, doGetFloorPlan)
}

function floorPlanArtworksToSaveArtworks(artworks: FloorPlanArtwork[]) {
    return artworks.map((a) => ({
        uuid: a.mapRefUUID,
        position: {
            x: a.x,
            y: a.y,
            side: a.side
        },
        frame: {
            has_frame: a.hasFrame,
            frame_width: a.frameWidth,
            frame_color: a.frameColor,
            frame_depth: a.depth,
            passepartout_width: a.passepartoutWidth,
            passepartout_color: a.passepartoutColor,
            edge_color: a.edgeColor,
            frame_texture_id: a.frameTextureId,
            frame_material: a.frameMaterial
        },
        exhibition_data: {
            width: a.width,
            height: a.height
        },
        remove: a.deleted,
    }))
}

function floorPlanWallsToSaveWalls(floorPlanWalls: Wall[], allArtworks: FloorPlanArtwork[], baseBoard: boolean) {
    return floorPlanWalls.map((w) => ({
        uuid: w.uuid,
        map_ref_uuid: w.mapRefUUID,
        name: w.name,
        x1: w.origin.x,
        x2: w.destiny.x,
        y1: w.origin.y,
        y2: w.destiny.y,
        thickness: w.thickness,
        color: w.color,
        has_color_back: w.hasColorBack,
        color_back: w.colorBack,
        remove: w.deleted,
        baseBoard,
        region: w.region,
        artworks: floorPlanArtworksToSaveArtworks(allArtworks.filter((a) => a.wallId === w.mapRefUUID))
    }))
}

function floorPlanLightsToSaveLights(lights: Light[]) {
    return lights.map((l) => ({
        uuid: l.uuid,
        map_ref_uuid: l.mapRefUUID,
        type: l.type,
        x: l.x,
        y: l.y,
        name: l.name,
        color: l.color,
        intensity: l.intensity,
        orientation: l.orientation,
        opening_angle: l.openingAngle,
        blurry_halo_pr: l.blurryHaloPr,
        vertical_angle: l.verticalAngle,
        remove: l.deleted,
    }))
}

function floorPlanSpotsToSaveSpots(spots: Spot[]) {
    return spots.map((s) => ({
        name: s.name,
        x: s.x,
        y: s.y,
        uuid: s.uuid,
        map_ref_uuid: s.mapRefUUID,
        type: s.type,
        orientation: s.orientation,
        artwork_uuid: s.artworkUUID,
        remove: s.deleted,
    }))
}

function floorPlanSculpturesToSaveSculptures(sculptures: Sculpture[]) {
    return sculptures.map((s) => ({
        x: s.x,
        y: s.y,
        z: s.z,
        uuid: s.uuid,
        map_ref_uuid: s.mapRefUUID,
        orientation: s.orientation,
        width_in_exhibition: s.dimensions.width,
        height_in_exhibition: s.dimensions.height,
        depth_in_exhibition: s.dimensions.depth,
        remove: s.deleted,
    }))
}

function* setFloorPlanUuids(floorPlanData: any, floorplan: FloorPlanMap) {
    const noUuidWalls = Object.values(floorplan.walls).filter(w => w.uuid === null)
    const walls: any[] = floorPlanData.walls
    if (!walls)
        throw new Error("Error saving map.")

    for (let i = 0; i < noUuidWalls.length; i++) {
        const wall = noUuidWalls[i]
        const uuid = walls.find((w) => w.map_ref_uuid === wall.mapRefUUID)?.uuid
        // console.log("setting wall uuid", wall.name, wall.mapRefUUID, uuid)
        if (uuid)
            yield put(setPropertyUuid({ id: wall.mapRefUUID, type: "Wall", uuid }))
    }

    const noUuidSpots = Object.values(floorplan.spots).filter(s => s.uuid === null)
    const spots: any[] = floorPlanData.spots
    if (!spots)
        throw new Error("Error saving map.")

    for (let i = 0; i < noUuidSpots.length; i++) {
        const spot = noUuidSpots[i]
        const uuid = spots.find((w) => w.map_ref_uuid === spot.mapRefUUID)?.uuid
        // console.log("setting wall uuid", spot.name, spot.mapRefUUID, uuid)
        if (uuid)
            yield put(setPropertyUuid({ id: spot.mapRefUUID, type: "Spot", uuid }))
    }

    const noUuidLights = Object.values(floorplan.lights).filter(s => s.uuid === null)
    const lights: any[] = floorPlanData.lights
    if (!lights)
        throw new Error("Error saving map.")

    for (let i = 0; i < noUuidLights.length; i++) {
        const light = noUuidLights[i]
        const uuid = lights.find((w) => w.map_ref_uuid === light.mapRefUUID)?.uuid
        // console.log("setting wall uuid", light.name, light.mapRefUUID, uuid)
        if (uuid)
            yield put(setPropertyUuid({ id: light.mapRefUUID, type: "Light", uuid }))
    }
}

function* doSaveFloorPlan() {
    const floorplanState: FloorPlanState | null = yield select((state: RootState) => state.floorPlan)
    const floorplan = floorplanState?.floorPlan
    const floorplanSettings = floorplanState?.floorPlanSettings

    if (floorplanState === null || floorplanState === undefined ||
        floorplan === null || floorplan === undefined ||
        floorplanSettings === null || floorplanSettings === undefined
    ) {
        // console.log("SAVE FLOOR PLAN REQUEST NO FLOORPLAN")
        yield delay(3000)

        const authStatus: AuthStatus = yield select((state: RootState) => state.auth.status)

        if (authStatus !== "invalid_token")
            yield put(saveFloorPlanRequest())

        return
    }


    try {
        yield put(setSaveState('SAVING'))
        // const mapResponse: AxiosResponse = yield call(callApi, 'PUT', `/maps/${floorplan.id}`,
        const jsonData = {
            "walls": floorPlanWallsToSaveWalls(Object.values(floorplan.walls).filter(e => e.placed).filter(e => !e.deleted), Object.values(floorplan.artworks), floorplanSettings.wallsBaseboard),
            "lights": floorPlanLightsToSaveLights(Object.values(floorplan.lights).filter(e => e.placed).filter(e => !e.deleted)),
            "spots": floorPlanSpotsToSaveSpots(Object.values(floorplan.spots).filter(e => e.placed).filter(e => !e.deleted)),
            "sculptures": floorPlanSculpturesToSaveSculptures(Object.values(floorplan.sculptures).filter(e => e.placed).filter(e => !e.deleted)),
            "entrance": {
                x: floorplan.entrance.x,
                y: floorplan.entrance.y,
                angle: floorplan.entrance.orientation
            },
            "has_floor": floorplanSettings.hasFloor,
            "floor_color": floorplanSettings.floorColor,
            "floor_has_texture": floorplanSettings.floorHasTexture,
            "floor_texture_id": floorplanSettings.floorTextureId,
            "ground_color": floorplanSettings.groundColor,
            "has_ceil": floorplanSettings.hasCeil,
            "ceil_color": floorplanSettings.ceilColor,
            "wall_height": floorplanSettings.wallHeight,
            "ambient_light_color": floorplanSettings.ambientLightColor,
        }

        // if (!isSuccessResponse(mapResponse, true)) {
        //     console.error(mapResponse)
        //     yield put(setSaveState('SAVE_ERROR'))
        //     throw new Error('Unable to save map.')
        // }

        // console.log("SAVE FLOOR PLAN REQUEST GOOD")
        yield put(setSaveState('UP_TO_DATE'))


        const myJsonStr = JSON.stringify(jsonData);
        var headerFriendlyStr = Buffer.from(myJsonStr, 'utf8').toString('base64');

        var numEquals = 0
        var lastChar = ''
        while ((lastChar = headerFriendlyStr.slice(-1)) === "=") {
            headerFriendlyStr = headerFriendlyStr.slice(0, -1)
            // console.log(lastChar)
            numEquals++
        }


        // console.log(headerFriendlyStr, numEquals)

        yield put(setShowDemoJsonData(headerFriendlyStr + numEquals))
        // set uuids
        // yield setFloorPlanUuids(mapResponse.data.data, floorplan)
    }
    catch (error) {
        yield put(setSaveState('SAVE_ERROR'))

        if (error instanceof Error) {
            console.error(error.message)
        } else {
            console.error('Unexpected error', error);
        }
    }
}

export function* watchSaveFloorPlan() {
    yield takeLatest(saveFloorPlanRequest, doSaveFloorPlan)
}


function* doStartSaveFloorPlanCycle() {
    // console.log("START SAVE CYCLE")

    const floorplanState: FloorPlanState = yield select((state: RootState) => state.floorPlan)

    if (floorplanState.autoSave && (floorplanState.saveState === "CHANGES_TO_SAVE" || floorplanState.saveState === "SAVE_ERROR")) {

        const floorplan = floorplanState?.floorPlan
        const floorplanSettings = floorplanState?.floorPlanSettings

        if (floorplan === null || floorplan === undefined ||
            floorplanSettings === null || floorplanSettings === undefined
        ) {
            // console.log("SAVE FLOOR PLAN REQUEST NO FLOORPLAN")
            yield delay(3000)

            const authStatus: AuthStatus = yield select((state: RootState) => state.auth.status)

            if (authStatus !== "invalid_token")
                yield put(startSaveFloorPlanCycle())

        }

        yield doSaveFloorPlan()
    }
    yield delay(30000)
    yield put(startSaveFloorPlanCycle())
}

export function* watchStartSaveFloorPlanCycle() {
    yield takeLatest(startSaveFloorPlanCycle, doStartSaveFloorPlanCycle)
}

function* doTryDeleteData() {
    const currentTool: ToolType = yield select((state: RootState) => state.editor.currentTool.type)
    if (currentTool !== "Select" && currentTool !== "PlaceArtworks")
        return

    const selectedData: SelectedData[] = yield select((state: RootState) => state.editor.selectedData)


    if (selectedData.length === 0)
        return

    if (currentTool === "PlaceArtworks" && selectedData[0].type === "Wall")
        return

    if (selectedData.length === 1 && selectedData[0].type === "Entrance")
        return

    yield put(confirmDeleteModalOpen())
}

export function* watchTryDeleteData() {
    yield takeLatest(tryDeleteData, doTryDeleteData)
}


const offlineMap = {
    "data": {
        "created_at": "2023-06-20T08:03:55.000000Z",
        "updated_at": "2023-09-26T12:48:25.000000Z",
        "deleted_at": null,
        "uuid": "e5aedb22-c673-46db-a291-7a4fdbd4ef47",
        "has_ceil": true,
        "walls_default_color": "#ffffff",
        "floor_color": "#3D3D3D",
        "ceil_color": "#cccccc",
        "tile_size": 50,
        "wall_height": 350,
        "ceil_has_texture": 0,
        "ceil_texture_url": null,
        "floor_has_texture": 0,
        "walls_have_default_texture": 0,
        "walls_default_texture_url": "empty",
        "has_floor": true,
        "floor_texture_id": "2502",
        "sky_color": "#777777",
        "ground_color": "#999999",
        "ambient_light_color": "#c7c7c7",
        "sculptures": [
            {
                "uuid": "f040c673-e1ff-46b4-bf01-e18c832eb763",
                "created_at": "2023-03-16T14:48:06.000000Z",
                "updated_at": "2023-09-26T12:42:39.000000Z",
                "deleted_at": null,
                "account": "c5a822bb-6acf-48d9-bbb8-bbd4671a9094",
                "title": {
                    "en_EN": "Greco-Roman Renaissance",
                    "es_ES": "titulo artwork 2"
                },
                "width": 100,
                "height": 220,
                "depth": 80,
                "unit": "cm",
                "year": "2019",
                "price": 56.97,
                "currency": "EUR",
                "technique": {
                    "en_EN": "digital art",
                    "es_ES": "tecnica artwork 2"
                },
                "has_variable_dimensions": 0,
                "is_unique": 0,
                "edition_total": 1,
                "edition_number": 1,
                "description": {
                    "en_EN": "Greco-Roman Renaissance sculpture",
                    "es_ES": "descripcion artwork 2"
                },
                "media_type": "SCULPTURE",
                "type": "SCULPTURE",
                "is_sold": 0,
                "has_info_dimensions": 0,
                "info_width": 0,
                "info_height": 0,
                "info_depth": 0,
                "sculpture_in_exhibition": {
                    "id_exhibition": 1,
                    "id_artwork": 2,
                    "x": "400.00",
                    "y": "400.00",
                    "z": "0.00",
                    "orientation": "TOP_LEFT",
                    "width_in_exhibition": "100.00",
                    "height_in_exhibition": "220.00",
                    "depth_in_exhibition": "80.00"
                },
                "photo": {
                    "uuid": "720b18ce-80f9-4a58-9611-38c30287c41b",
                    "file_name": null,
                    "original": "https://i.gyazo.com/c7717736e600373b53b85a87be66d4c5.png",
                    "large": "https://api.exhibify.net/image_process/resize.php?src=https://i.gyazo.com/c7717736e600373b53b85a87be66d4c5.png&w=1200",
                    "medium": "https://api.exhibify.net/image_process/resize.php?src=https://i.gyazo.com/c7717736e600373b53b85a87be66d4c5.png&w=800",
                    "small": "https://api.exhibify.net/image_process/resize.php?src=https://i.gyazo.com/c7717736e600373b53b85a87be66d4c5.png&w=400"
                },
                "photos": [],
                "artist": {
                    "firstname": "Ana",
                    "lastname": "Haust",
                    "web": "https://www.kautzer.org/corrupti-est-pariatur-illum-accusamus-et-reprehenderit-aspernatur",
                    "uuid": "8a39ee77-7c65-4dbd-8d76-700abe4c5ef0",
                    "created_at": "2023-03-16T14:48:06.000000Z",
                    "updated_at": "2023-03-16T14:48:06.000000Z",
                    "deleted_at": null,
                    "account": "c5a822bb-6acf-48d9-bbb8-bbd4671a9094"
                },
                "video": null,
                "sculpture": {
                    "id_account": 1,
                    "uuid": "647d8aab-00e2-4d7f-b58b-795352727dab",
                    "file_url": "https://exb-images.s3.eu-west-1.amazonaws.com/8zIR2rvfLNIgtunxDfNyrKaRC.fbx",
                    "created_at": "2023-09-26T12:42:38.000000Z",
                    "updated_at": "2023-09-26T12:42:41.000000Z",
                    "file_name": "Greco-Roman_Renaissance_grey.fbx",
                    "upload_status": "uploaded",
                    "uploaded_at": "2023-09-26 12:42:41"
                },
                "main_2d_photo": null
            }
        ],
        "account": "c5a822bb-6acf-48d9-bbb8-bbd4671a9094",
        "walls": [
            {
                "name": "Wall 1",
                "width": 0,
                "height": null,
                "thickness": 10,
                "x1": 150,
                "y1": -300,
                "x2": 500,
                "y2": -300,
                "angle": 0,
                "color": "#ffffff",
                "texture": null,
                "uuid": "a96a7e7e-d350-46e5-96f4-0c99ffefc9f8",
                "has_texture": 0,
                "created_at": "2023-09-21T08:15:19.000000Z",
                "updated_at": "2023-09-26T12:48:25.000000Z",
                "map_ref_uuid": "ab6a84b6-8f9f-4817-86bc-38181e9a199f",
                "has_color_back": 0,
                "color_back": "#ffffff",
                "corner": null,
                "direction": "",
                "baseboard": false,
                "artwork": [
                    {
                        "uuid": "6584f49f-251b-4904-8caa-29ddce35b344",
                        "created_at": "2023-03-16T14:48:06.000000Z",
                        "updated_at": "2023-09-22T11:18:00.000000Z",
                        "deleted_at": null,
                        "account": "c5a822bb-6acf-48d9-bbb8-bbd4671a9094",
                        "title": {
                            "en_EN": "Fluid video",
                            "es_ES": "titulo artwork 4"
                        },
                        "width": 160,
                        "height": 90,
                        "depth": 5,
                        "unit": "cm",
                        "year": "2004",
                        "price": 13.16,
                        "currency": "EUR",
                        "technique": {
                            "en_EN": "fluid simulation",
                            "es_ES": "tecnica artwork 4"
                        },
                        "has_variable_dimensions": 0,
                        "is_unique": 0,
                        "edition_total": 1,
                        "edition_number": 1,
                        "description": {
                            "en_EN": "This is a sample video to showcase a custom uploaded video",
                            "es_ES": "descripcion artwork 4"
                        },
                        "media_type": "VIDEO",
                        "type": "PAINTING",
                        "is_sold": 0,
                        "has_info_dimensions": 0,
                        "info_width": 0,
                        "info_height": 0,
                        "info_depth": 0,
                        "artwork_in_exhibition": {
                            "id_exhibition": 1,
                            "id_artwork": 4,
                            "x": "95.00",
                            "y": "145.40",
                            "side": "right",
                            "id_wall": 319,
                            "has_frame": 0,
                            "frame_width": "0.00",
                            "frame_color": "#121212",
                            "passepartout_width": "0.00",
                            "passepartout_color": "#ffffff",
                            "width_in_exhibition": "160.00",
                            "height_in_exhibition": "90.00",
                            "order": null,
                            "frame_depth": "5.00",
                            "edge_color": "#bcbcbc",
                            "frame_texture_id": null,
                            "frame_material": "0001"
                        },
                        "photo": {
                            "uuid": "e0cf612f-4a14-48e6-827a-a0f111efe54e",
                            "file_name": null,
                            "original": "https://i.gyazo.com/c13efaebab7601d9ca522432d1b19efb.jpg",
                            "large": "https://api.exhibify.net/image_process/resize.php?src=https://i.gyazo.com/c13efaebab7601d9ca522432d1b19efb.jpg&w=1200",
                            "medium": "https://api.exhibify.net/image_process/resize.php?src=https://i.gyazo.com/c13efaebab7601d9ca522432d1b19efb.jpg&w=800",
                            "small": "https://api.exhibify.net/image_process/resize.php?src=https://i.gyazo.com/c13efaebab7601d9ca522432d1b19efb.jpg&w=400"
                        },
                        "photos": [],
                        "artist": {
                            "firstname": "Oladimeji",
                            "lastname": "Odunsi",
                            "web": "https://www.sipes.com/dolorem-aliquam-quas-amet-nulla-molestias-aut-aut",
                            "uuid": "12779a6f-854d-470d-8caf-2215040a01e3",
                            "created_at": "2023-03-16T14:48:06.000000Z",
                            "updated_at": "2023-03-16T14:48:06.000000Z",
                            "deleted_at": null,
                            "account": "c5a822bb-6acf-48d9-bbb8-bbd4671a9094"
                        },
                        "video": {
                            "id": 4,
                            "id_account": 1,
                            "uuid": "411747e7-0508-4574-906c-ba6afb380d18",
                            "url": "",
                            "duration": "00:00:00",
                            "title": null,
                            "thumbnail_url": null,
                            "created_at": "2023-03-23T17:32:48.000000Z",
                            "updated_at": "2023-03-23T17:32:55.000000Z",
                            "thumbnail_url_low": null,
                            "thumbnail_url_high": null,
                            "mute": 0,
                            "auto_start": 1,
                            "file_url": "https://exb-images.s3.eu-west-1.amazonaws.com/IgXPsIarZp3hDDgKjp1kJFo0u.mp4",
                            "upload_status": "uploaded",
                            "uploaded_at": "2023-03-23 17:32:55",
                            "file_name": "CubeReem 30 sec test @ 4K - Copy.mp4"
                        },
                        "sculpture": null,
                        "main_2d_photo": null
                    }
                ]
            },
            {
                "name": "Wall 2",
                "width": 0,
                "height": null,
                "thickness": 10,
                "x1": 500,
                "y1": -300,
                "x2": 500,
                "y2": 500,
                "angle": 0,
                "color": "#ffffff",
                "texture": null,
                "uuid": "da7b44a4-8808-43c4-8523-f021651ed78b",
                "has_texture": 0,
                "created_at": "2023-09-21T08:16:03.000000Z",
                "updated_at": "2023-09-26T12:48:25.000000Z",
                "map_ref_uuid": "9a4fc990-e2ae-4245-bb14-bf9de6b94f9b",
                "has_color_back": 0,
                "color_back": "#ffffff",
                "corner": null,
                "direction": "",
                "baseboard": false,
                "artwork": [
                    {
                        "uuid": "85877346-35f3-4458-bbd3-fc9ae110bf13",
                        "created_at": "2023-03-16T14:48:06.000000Z",
                        "updated_at": "2023-03-23T17:32:49.000000Z",
                        "deleted_at": null,
                        "account": "c5a822bb-6acf-48d9-bbb8-bbd4671a9094",
                        "title": {
                            "en_EN": "Cyan titulo largo test de un titulo largo. largo es el titulo para hacer pruebas de un titulo largo",
                            "es_ES": "titulo artwork 3"
                        },
                        "width": 84,
                        "height": 104,
                        "depth": 0,
                        "unit": "cm",
                        "year": "2016",
                        "price": 2500,
                        "currency": "EUR",
                        "technique": {
                            "en_EN": " tecnica del artwork",
                            "es_ES": "tecnica artwork 3"
                        },
                        "has_variable_dimensions": 0,
                        "is_unique": 0,
                        "edition_total": 1,
                        "edition_number": 1,
                        "description": {
                            "en_EN": "001 descripcionCurabitur facilisis justo arcu, sed tempus elit cursus vitae. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Duis lacinia ultricies est eget dignissim. Mauris consequat tincidunt sapien, et egestas velit. Quisque eu lorem et tortor lobortis suscipit vel at nunc. Proin maximus elit sit amet metus placerat cursus. Curabitur commodo, purus non suscipit accumsan, est purus euismod velit, iaculis luctus lorem dolor non nulla. Praesent auctor tincidunt lorem vel imperdiet. Vestibulum commodo ipsum a massa dictum, et ultrices urna hendrerit. Sed fermentum odio vitae sem convallis, vitae pellentesque ex tincidunt.Suspendisse laoreet euismod nisl vitae pellentesque. Morbi venenatis lorem erat, vitae luctus sapien scelerisque nec. Maecenas vitae tellus ac lorem rhoncus pharetra vitae vel nisl. Morbi tellus justo, congue iaculis vehicula vel, mattis ac ex. Pellentesque non tellus sed quam fringilla elementum sed a purus. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Nam rhoncus eros non augue laoreet, nec tristique mi commodo. In sollicitudin nunc arcu, a fermentum dui ullamcorper et. Vivamus sed faucibus velit. Nullam vestibulum est a condimentum sollicitudin. Nulla efficitur consequat ullamcorper. Vivamus quis erat in libero vehicula porttitor. Sed eu sollicitudin ipsum, dictum convallis arcu.",
                            "es_ES": "descripcion artwork 3"
                        },
                        "media_type": "IMAGE",
                        "type": "PAINTING",
                        "is_sold": 0,
                        "has_info_dimensions": 0,
                        "info_width": 0,
                        "info_height": 0,
                        "info_depth": 0,
                        "artwork_in_exhibition": {
                            "id_exhibition": 1,
                            "id_artwork": 3,
                            "x": "350.00",
                            "y": "110.40",
                            "side": "right",
                            "id_wall": 320,
                            "has_frame": 1,
                            "frame_width": "5.00",
                            "frame_color": "#ffffff",
                            "passepartout_width": "0.00",
                            "passepartout_color": "#ffffff",
                            "width_in_exhibition": "120.00",
                            "height_in_exhibition": "150.00",
                            "order": null,
                            "frame_depth": "0.00",
                            "edge_color": "#bcbcbc",
                            "frame_texture_id": null,
                            "frame_material": "0001"
                        },
                        "photo": {
                            "uuid": "e9539737-7f4b-4c2d-ace3-e3aba22d3cc9",
                            "file_name": null,
                            "original": "https://dncs92qkcz9jn.cloudfront.net/works/218333/Oladimeji_Odunsi_1.jpg",
                            "large": "https://api.exhibify.net/image_process/resize.php?src=https://dncs92qkcz9jn.cloudfront.net/works/218333/Oladimeji_Odunsi_1.jpg&w=1200",
                            "medium": "https://api.exhibify.net/image_process/resize.php?src=https://dncs92qkcz9jn.cloudfront.net/works/218333/Oladimeji_Odunsi_1.jpg&w=800",
                            "small": "https://api.exhibify.net/image_process/resize.php?src=https://dncs92qkcz9jn.cloudfront.net/works/218333/Oladimeji_Odunsi_1.jpg&w=400"
                        },
                        "photos": [],
                        "artist": {
                            "firstname": "Oladimeji",
                            "lastname": "Odunsi",
                            "web": "https://www.sipes.com/dolorem-aliquam-quas-amet-nulla-molestias-aut-aut",
                            "uuid": "12779a6f-854d-470d-8caf-2215040a01e3",
                            "created_at": "2023-03-16T14:48:06.000000Z",
                            "updated_at": "2023-03-16T14:48:06.000000Z",
                            "deleted_at": null,
                            "account": "c5a822bb-6acf-48d9-bbb8-bbd4671a9094"
                        },
                        "video": {
                            "id": 6,
                            "id_account": 1,
                            "uuid": "774dabda-f85f-4091-b59d-2bc436521b5a",
                            "url": "",
                            "duration": "00:00:00",
                            "title": null,
                            "thumbnail_url": null,
                            "created_at": "2023-09-22T11:17:59.000000Z",
                            "updated_at": "2023-09-22T11:18:03.000000Z",
                            "thumbnail_url_low": null,
                            "thumbnail_url_high": null,
                            "mute": 0,
                            "auto_start": 1,
                            "file_url": "https://exb-images.s3.eu-west-1.amazonaws.com/99bQTbFbV6gforEo3V0Jrta2l.mp4",
                            "upload_status": "uploaded",
                            "uploaded_at": "2023-09-22 11:18:03",
                            "file_name": "fluid_2.mp4"
                        },
                        "sculpture": null,
                        "main_2d_photo": null
                    },
                    {
                        "uuid": "36ef80ca-5534-488b-a200-a19e04cd906e",
                        "created_at": "2023-03-16T14:48:06.000000Z",
                        "updated_at": "2023-03-16T14:48:06.000000Z",
                        "deleted_at": null,
                        "account": "c5a822bb-6acf-48d9-bbb8-bbd4671a9094",
                        "title": {
                            "en_EN": "Black & Red"
                        },
                        "width": 100,
                        "height": 150,
                        "depth": 5,
                        "unit": "cm",
                        "year": "1991",
                        "price": 30.72,
                        "currency": "EUR",
                        "technique": {
                            "en_EN": ""
                        },
                        "has_variable_dimensions": 0,
                        "is_unique": 0,
                        "edition_total": 1,
                        "edition_number": 1,
                        "description": {
                            "en_EN": "\"Black is” is a project that focuses on the positives of being black and African. The project comprises of a mixture of African attires and accessories, to enlighten people outside of these communities of what being black or African is all about. Black symbolises Love, Beauty, Power, Strength, Endurance, Authority, Excellence, Vulnerability, Uniqueness, Resilience and so much more which can be portrayed in beautiful ways but are rarely showcased."
                        },
                        "media_type": "IMAGE",
                        "type": "PAINTING",
                        "is_sold": 0,
                        "has_info_dimensions": 0,
                        "info_width": 0,
                        "info_height": 0,
                        "info_depth": 0,
                        "artwork_in_exhibition": {
                            "id_exhibition": 1,
                            "id_artwork": 5,
                            "x": "140.00",
                            "y": "115.40",
                            "side": "right",
                            "id_wall": 320,
                            "has_frame": 0,
                            "frame_width": "0.00",
                            "frame_color": "#121212",
                            "passepartout_width": "0.00",
                            "passepartout_color": "#ffffff",
                            "width_in_exhibition": "100.00",
                            "height_in_exhibition": "150.00",
                            "order": null,
                            "frame_depth": "10.00",
                            "edge_color": "#bcbcbc",
                            "frame_texture_id": null,
                            "frame_material": "0001"
                        },
                        "photo": {
                            "uuid": "6d2dafec-25ec-41b4-9f62-db2cd0830d81",
                            "file_name": null,
                            "original": "https://dncs92qkcz9jn.cloudfront.net/works/296932/lucas-gouvea-aoEwuEH7YAs-unsplash.jpg",
                            "large": "https://api.exhibify.net/image_process/resize.php?src=https://dncs92qkcz9jn.cloudfront.net/works/296932/lucas-gouvea-aoEwuEH7YAs-unsplash.jpg&w=1200",
                            "medium": "https://api.exhibify.net/image_process/resize.php?src=https://dncs92qkcz9jn.cloudfront.net/works/296932/lucas-gouvea-aoEwuEH7YAs-unsplash.jpg&w=800",
                            "small": "https://api.exhibify.net/image_process/resize.php?src=https://dncs92qkcz9jn.cloudfront.net/works/296932/lucas-gouvea-aoEwuEH7YAs-unsplash.jpg&w=400"
                        },
                        "photos": [],
                        "artist": {
                            "firstname": "Regan",
                            "lastname": "Welch",
                            "web": "http://osinski.biz/dolor-neque-officia-nulla-officia-qui-porro-rerum.html",
                            "uuid": "24f7ebbb-9a72-4a90-8258-022c51a7e58c",
                            "created_at": "2023-03-16T14:48:06.000000Z",
                            "updated_at": "2023-03-16T14:48:06.000000Z",
                            "deleted_at": null,
                            "account": "c5a822bb-6acf-48d9-bbb8-bbd4671a9094"
                        },
                        "video": null,
                        "sculpture": null,
                        "main_2d_photo": null
                    }
                ]
            },
            {
                "name": "Wall 3",
                "width": 0,
                "height": null,
                "thickness": 10,
                "x1": 500,
                "y1": 500,
                "x2": 150,
                "y2": 500,
                "angle": 0,
                "color": "#ffffff",
                "texture": null,
                "uuid": "3e2a18c8-af3f-416b-ae58-7416032f04b1",
                "has_texture": 0,
                "created_at": "2023-09-21T08:16:03.000000Z",
                "updated_at": "2023-09-26T12:48:25.000000Z",
                "map_ref_uuid": "b6be70fc-3da8-4351-a4d0-e3a0f41b9d9c",
                "has_color_back": 0,
                "color_back": "#ffffff",
                "corner": null,
                "direction": "",
                "baseboard": false,
                "artwork": []
            }
        ],
        "lights": [
            {
                "x": 200,
                "y": -100,
                "orientation": "RIGHT",
                "type": "SPOTLIGHT",
                "intensity": 0.95,
                "color": "#ffffff",
                "uuid": "75801e65-6b43-4675-ad53-bdaf729e067f",
                "name": "Light 1",
                "created_at": "2023-09-21T11:53:04.000000Z",
                "updated_at": "2023-09-22T13:14:24.000000Z",
                "map_ref_uuid": "fbbea703-d352-4280-b910-a5028655ce66",
                "opening_angle": "70.00",
                "blurry_halo_pr": 50,
                "vertical_angle": 45
            },
            {
                "x": 200,
                "y": 100,
                "orientation": "RIGHT",
                "type": "SPOTLIGHT",
                "intensity": 0.95,
                "color": "#ffffff",
                "uuid": "5c549905-7fff-4e25-9a21-20fdf2bd33e4",
                "name": "Light 2",
                "created_at": "2023-09-21T11:53:04.000000Z",
                "updated_at": "2023-09-22T13:13:53.000000Z",
                "map_ref_uuid": "9c48b2dd-d879-4201-9a01-9ae0fe3b20c3",
                "opening_angle": "70.00",
                "blurry_halo_pr": 50,
                "vertical_angle": 45
            },
            {
                "x": 300,
                "y": 300,
                "orientation": "BOTTOM_RIGHT",
                "type": "SPOTLIGHT",
                "intensity": 0.45,
                "color": "#ffffff",
                "uuid": "e59cd80f-74f1-4031-99bf-64f896c0be3f",
                "name": "Light 3",
                "created_at": "2023-09-22T09:45:51.000000Z",
                "updated_at": "2023-09-22T16:51:33.000000Z",
                "map_ref_uuid": "ebc0ed16-44df-475f-8861-ca23a1f57372",
                "opening_angle": "70.00",
                "blurry_halo_pr": 50,
                "vertical_angle": 55
            }
        ],
        "spots": [
            {
                "x": 250,
                "y": 250,
                "orientation": "BOTTOM_RIGHT",
                "type": "MOVEMENT",
                "uuid": "12e7bcd6-0db8-4185-ac57-16489f248536",
                "name": "Spot 1",
                "map_ref_uuid": "7a542294-1a8d-4f10-8b21-6ad668c101f7",
                "created_at": "2023-09-22T09:37:22.000000Z",
                "updated_at": "2023-09-22T11:20:58.000000Z",
                "artwork_uuid": null
            },
            {
                "x": 300,
                "y": 100,
                "orientation": "RIGHT",
                "type": "MOVEMENT",
                "uuid": "e01cb245-14b9-4ba0-9e27-cc63c4e57eb2",
                "name": "Spot 2",
                "map_ref_uuid": "71a45f10-eb3f-4389-895d-3a4291e48934",
                "created_at": "2023-09-22T09:38:23.000000Z",
                "updated_at": "2023-09-22T13:21:56.000000Z",
                "artwork_uuid": null
            },
            {
                "x": 300,
                "y": -100,
                "orientation": "RIGHT",
                "type": "MOVEMENT",
                "uuid": "32a97e78-06e3-45b7-aac0-e5ed14a140a5",
                "name": "Spot 3",
                "map_ref_uuid": "c306919f-d6fe-4332-93f9-054c6fd824a6",
                "created_at": "2023-09-22T09:38:23.000000Z",
                "updated_at": "2023-09-22T13:14:24.000000Z",
                "artwork_uuid": "85877346-35f3-4458-bbd3-fc9ae110bf13"
            }
        ],
        "floor_texture": {
            "name": "Jasper Grey",
            "type": "carpet",
            "default_color": "#3D3D3D",
            "default_smoothness": "0.000",
            "url": " ",
            "preview_url": "",
            "height_url": null,
            "roughness_url": null,
            "ambient_occlusion_url": "https://d22j8f47u6lqy6.cloudfront.net/materials/carpets/carpet_jasper_gray/carpet_gris_jaspe_AO.jpg",
            "color_url": "https://d22j8f47u6lqy6.cloudfront.net/materials/carpets/carpet_jasper_gray/carpet_gris_jaspe_Albedo+2.jpg",
            "normal_url": "https://d22j8f47u6lqy6.cloudfront.net/materials/carpets/carpet_jasper_gray/carpet_gris_jaspe_Normal.jpg",
            "created_at": "2023-05-16T13:32:13.000000Z",
            "updated_at": "2023-05-16T13:32:13.000000Z",
            "id": "2502"
        },
        "exhibition": {
            "title": {
                "en_EN": "exhibition title",
                "es_ES": "titulo de la exhibicion"
            },
            "description": {
                "en_EN": "exhibition description",
                "es_ES": "descripcion de la exhbiicion"
            },
            "url": "Ab sit doloremque consectetur iusto et iste.",
            "uuid": "a605bb9e-a6cb-4cd7-a712-4c5737e3652d",
            "status": "draft",
            "id_photo": null,
            "entrance_x": -200,
            "entrance_y": 100,
            "entrance_angle": "RIGHT",
            "start_date": null,
            "end_date": null,
            "created_at": "2023-03-16T14:48:06.000000Z",
            "updated_at": "2023-09-22T13:07:41.000000Z",
            "deleted_at": null,
            "created_by_id": null,
            "is_2d": 0,
            "final_url": null,
            "last_version": 0,
            "account": "c5a822bb-6acf-48d9-bbb8-bbd4671a9094",
            "map": "e5aedb22-c673-46db-a291-7a4fdbd4ef47",
            "thumbnail": null,
            "meta": []
        }
    }
}